import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { combineLatest, finalize, take, timer } from 'rxjs';
import { AlertLogSummary } from 'src/app/modules/alerting/model/alert-log-summary.model';
import { InterventionAlertingMessage } from 'src/app/modules/intervention/models/intervention-alertingmessage-model';
import { AvailabilityCalculationItem } from 'src/app/modules/intervention/models/intervention-availability-calculation-item.model';
import { ExportConfig } from 'src/app/modules/intervention/models/intervention-export-config.model';
import InterventionFilter from 'src/app/modules/intervention/models/intervention-filter.model';
import { FireService } from 'src/app/modules/intervention/models/intervention-fireservice.model';
import { InterventionMedium } from 'src/app/modules/intervention/models/intervention-medium.model';
import { InterventionPerson } from 'src/app/modules/intervention/models/intervention-person.model';
import InterventionReport from 'src/app/modules/intervention/models/intervention-report.model';
import { InterventionXml } from 'src/app/modules/intervention/models/intervention-xml.model';
import { Intervention } from 'src/app/modules/intervention/models/intervention.model';
import { AvailabilityRun } from 'src/app/modules/simulation/model/availability-run.model';
import { UnitStatusHistorySummary } from 'src/app/modules/vehicle/model/unit-status-history-summary.model';
import { VehicleTrackAndTraceResponse } from 'src/app/modules/vehicle/model/vehicle-track-and-trace-response.model';
import { Event } from 'src/app/shared/model/event.model';
import { MetadataGroup } from 'src/app/shared/model/metadata/metadata-group.model';
import { PagedResult } from 'src/app/shared/model/page-result.model';
import { environment } from 'src/environments/environment';
import { IncidentCreateResult } from '../models/incident-create-result.model';
import { LocationBase } from '../models/location-base.model';
import { RequestDeploymentNote } from '../models/request-deployment-note.model';

@Injectable({
  providedIn: 'root',
})
export class InterventionService {
  private readonly BASE_URL = '/intervention';

  constructor(
    private readonly http: HttpClient,
    private readonly messageService: NzMessageService
  ) {}

  create$(request: {
    interventionCode: string;
    location: LocationBase;
    remarks?: string;
    callerName?: string;
    phoneNo?: string;
    isExercise: boolean;
    linkedInterventionId?: number;
    stationid?: number;
  }) {
    // Returns seqno
    return this.http.post<IncidentCreateResult>(`${this.BASE_URL}/create`, request);
  }

  search$(filter: InterventionFilter, maxResults: number, showOldInterventions: boolean) {
    let timeout = environment?.timeout ?? 60_000;
    if (maxResults > 5_000) {
      timeout = 120_000;
    }
    return this.http.post<Intervention[]>(
      `${this.BASE_URL}/search/${maxResults}/${showOldInterventions}`,
      JSON.stringify(filter),
      {
        headers: { timeout: timeout.toString() },
      }
    );
  }

  get$(interventionId: number) {
    return this.http.get<Intervention>(`${this.BASE_URL}/${interventionId}`);
  }

  getFireservice$(interventionId: number) {
    return this.http.get<FireService[]>(`${this.BASE_URL}/${interventionId}/fireservices`);
  }

  getXml$(interventionId: number) {
    return this.http.get<InterventionXml[]>(`${this.BASE_URL}/${interventionId}/xml`);
  }

  getInterventionPersons$(interventionId: number) {
    return this.http.get<InterventionPerson[]>(`${this.BASE_URL}/${interventionId}/interventionpersons`);
  }

  getUnitStatusHistory$(interventionId: number) {
    return this.http.get<PagedResult<UnitStatusHistorySummary>>(`${this.BASE_URL}/${interventionId}/unitstatushistory`);
  }

  getAlertingHistory$(interventionId: number) {
    return this.http.get<InterventionAlertingMessage[]>(`${this.BASE_URL}/${interventionId}/alertinghistory`);
  }

  getInterventionMedia$(interventionId: number) {
    return this.http.get<InterventionMedium[]>(`${this.BASE_URL}/${interventionId}/interventionmedia`);
  }

  getDeploymentnotes$(interventionId: number) {
    return this.http.get<InterventionMedium[]>(`${this.BASE_URL}/${interventionId}/deploymentnotes`);
  }

  reopenInterventions$(interventionIds: number[]) {
    return this.http.post<number[]>(`${this.BASE_URL}/reopen`, JSON.stringify(interventionIds));
  }

  localAssign$(request: object) {
    return this.http.post<number[]>(`${this.BASE_URL}/localisation`, JSON.stringify(request));
  }

  getMetadata$(interventionId: number) {
    return this.http.get<MetadataGroup[]>(`${this.BASE_URL}/${interventionId}/metadata`);
  }

  printDeploymentnote$(request: RequestDeploymentNote) {
    return this.http.post<number[]>(`${this.BASE_URL}/printdeploymentnote`, JSON.stringify(request));
  }

  getExportConfigs$() {
    return this.http.get<ExportConfig[]>(`${this.BASE_URL}/exportconfigs`);
  }

  exportInterventions$(request: object) {
    return this.http.post(`${this.BASE_URL}/export`, JSON.stringify(request));
  }

  getEvents$(interventionId: number) {
    return this.http.get<Event[]>(`${this.BASE_URL}/${interventionId}/events`);
  }

  getAvailabilityRuns$(interventionId: number) {
    return this.http.get<AvailabilityCalculationItem[]>(`${this.BASE_URL}/${interventionId}/availabilityruns`);
  }

  getAvailabilityRun$(interventionId: number, availabilityRunId: number) {
    return this.http.get<AvailabilityRun>(`${this.BASE_URL}/${interventionId}/availabilityrun/${availabilityRunId}`);
  }

  getAlertLogsIntervention$(id: number) {
    return this.http.get<PagedResult<AlertLogSummary>>(`${this.BASE_URL}/${id}/alerts`);
  }

  getInterventionReportExport(interventions: number[], toastMessage: string) {
    const message = this.messageService.loading(toastMessage);
    // Timer here is to have a minimal delay of 500ms so we can display a toast.
    combineLatest([
      this.http.post(`${this.BASE_URL}/report/archive`, interventions, { responseType: 'arraybuffer' }),
      timer(500),
    ])
      .pipe(
        take(1),
        finalize(() => {
          this.messageService.remove(message.messageId);
        })
      )
      .subscribe((data) => {
        let a = document.createElement('a');
        const blob = new Blob([data[0]], { type: 'application/zip' });
        a.href = window.URL.createObjectURL(blob);
        a.download = `reports_${dayjs().format('YYYY-MM-DD_HH-mm')}.zip`;
        a.click();
      });
  }

  getInterventionReport$(interventionId: number) {
    return this.http.get<InterventionReport>(`${this.BASE_URL}/${interventionId}/report`);
  }

  getTrackAndTrace$(interventionId: number) {
    return this.http.get<VehicleTrackAndTraceResponse>(`${this.BASE_URL}/${interventionId}/trackandtrace`);
  }

  upsertInterventionReport$(interventionId: number, report: InterventionReport) {
    return this.http.post<InterventionReport>(`${this.BASE_URL}/${interventionId}/report`, report);
  }

  sendTextXml$(xml: string) {
    const request = { xml };
    return this.http.post<IncidentCreateResult>(`${this.BASE_URL}/xmltester`, JSON.stringify(request));
  }
}
